<template>
    <div class="container">
        <span @click="$emit('close')" role="button"><small><i
                    class="fa-solid fa-chevron-left me-2"></i>{{ $t('common.back') }}</small></span>
        <h2 class="my-3">{{ isEditMode ? $t('modal.editComment') : (originalComment ? $t('modal.replyToComment') : $t('modal.addComment')) }}</h2>
        <div class="mb-3">
            <label for="commentInput" class="form-label"><small>{{ $t('modal.content') }}</small></label>
            <textarea id="commentInput" class="form-control bg-body-tertiary" v-model="comment"
                :placeholder="$t('modal.commentPlaceholder')"></textarea>
        </div>
        <div class="mb-3">
            <label class="form-label small">{{ $t('modal.notifyAboutComment') }}</label>
            <MultiSelect v-model="selectedUsers" :options="users" optionLabel="name" optionValue="id"
                :placeholder="$t('modal.selectUsers')" :maxSelectedLabels="3" filter class="w-100 bg-body-tertiary" :showToggleAll="false"
                :selectedItemsLabel="$t('modal.selectedCount')" />
        </div>
        <div class="row mt-3">
            <div class="col">
                <button @click="$emit('close')" class="btn btn-outline-primary w-100" :disabled="actionInProgress">{{ $t('common.cancel') }}</button>
            </div>
            <div class="col">
                <button @click="submitComment" class="btn btn-primary w-100" :disabled="actionInProgress">{{ isEditMode ? $t('common.save') : $t('modal.post') }}</button>
            </div>
        </div>
    </div>
</template>

<script>

import axios from '@/services/axios';
import MultiSelect from 'primevue/multiselect';

export default {
    components: {
        MultiSelect,
    },
    props: {
        taskId: {
            type: Number,
            required: true
        },
        originalComment: {
            type: Object,
            default: () => ({})
        },
        commentToEdit: {
            type: Object,
            default: null
        },
    },
    data() {
        return {
            comment: null,
            notify: false,
            users: [],
            selectedUsers: [],
            // Held only while the request is in flight, so a slow network
            // cannot turn one click into two records.
            actionInProgress: false,
        };
    },
    computed: {
        isEditMode() {
            return !!(this.commentToEdit && this.commentToEdit.id);
        },
    },
    mounted() {
        if (this.isEditMode) {
            // Comments are plaintext, but strip any HTML just in case so the
            // textarea shows clean text rather than raw markup.
            this.comment = this.stripHtml(this.commentToEdit.comment);
        }
        this.fetchUsers();
    },
    methods: {
        stripHtml(value) {
            const tmp = document.createElement('div');
            tmp.innerHTML = value || '';
            return tmp.textContent || tmp.innerText || '';
        },
        async submitComment() {
            if (!this.comment || this.comment.trim() === '') {
                return;
            }
            if (this.actionInProgress) return;
            this.actionInProgress = true;
            try {
                let response;
                if (this.isEditMode) {
                    // Editing keeps the notify feature so a user who forgot to
                    // notify anyone can do so now. Parent stays unchanged.
                    response = await axios.patch(`/tasks/${this.taskId}/comments/${this.commentToEdit.id}`, {
                        comment: this.comment,
                        users: this.selectedUsers, // already IDs
                        notify: true,
                    });
                } else {
                    const originalCommentId = this.originalComment ? this.originalComment.id : null;
                    response = await axios.post(`/tasks/${this.taskId}/comments`, {
                        parent_id: originalCommentId,
                        comment: this.comment,
                        users: this.selectedUsers, // already IDs
                        notify: true,
                    });
                }

                if (response.status === 200) {
                    this.$emit('comment-added');
                    this.$emit('close');
                }
            } catch (error) {
                console.error('Error saving comment:', error);

            } finally {
                this.actionInProgress = false;
            }
        },
        async fetchUsers() {
            let url = `/tasks/${this.taskId}/comments/users`;

            if (this.originalComment && this.originalComment.user_id) {
                url += `?users=${this.originalComment.user_id}`;
            }

            try {
                const response = await axios.get(url);
                this.users = response.data.data;
                this.selectedUsers = this.users.filter(u => u.selected).map(u => u.id);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        },
        toggleCheckbox(user) {
            const index = this.selectedUsers.indexOf(user.id);
            if (index > -1) {
                this.selectedUsers.splice(index, 1);
            } else {
                this.selectedUsers.push(user.id);
            }
        }
    }
}
</script>

<style>
.p-multiselect-items {
    padding: 0 !important;
}

.p-multiselect-label {
    font-size: 0.875rem;
    color: var(--bs-body-color);
    font-family: var(--bs-body-font-family);
}

.p-checkbox.p-highlight .p-checkbox-box {
    background: var(--bs-red);
    border: var(--bs-red);
}
</style>
